import { Box, HStack, Image, Text, VStack } from "@chakra-ui/react"
import {
  CreatableProps,
  CreatableSelect,
  FormatOptionLabelMeta,
  GroupBase,
  InputActionMeta,
  MultiValue,
  SelectInstance,
  SingleValue,
  useChakraSelectProps
} from "chakra-react-select"
import { useEffect, useMemo, useRef, useState } from "react"
import { GroupAvatars } from "../../unified-home/components/GroupAvatars"
import ItemInput from "./ItemInput"
import RoleBadge, {
  getRoleOptions,
  serializeRoleBadgesToRegistrationRoles
} from "./RoleBadge"
import {
  CustomSelectProps,
  getSelectCustomComponents
} from "./components/select"
import { SelectedCohort, SelectedResource, isExistingItem } from "./state"

// react-select doesn't export this type.
interface FilterOptionOption<Option> {
  readonly label: string
  readonly value: string
  readonly data: Option
}

type Option = SelectedCohort | SelectedResource

type ItemsProps<T extends Option> = {
  options?: T[]
  value?: T | null
  onChange: (option?: T | null) => void
  itemLabel: string
  isLoading: boolean
  // Whether the component can only create or select, or both.
  mode: "create" | "select" | "create-or-select"
}

// Pass any custom props to components
type SelectCustomProps = CustomSelectProps

export type ItemSelectProps<T extends Option> = Omit<
  Partial<SelectProps<T>>,
  "onChange"
> & {
  // Piggy back off of the creatable props. The parent component can provide
  // `getNewOptionData` to create a new item.
  getNewOptionData: Required<
    CreatableProps<T, boolean, GroupBase<T>>
  >["getNewOptionData"]
}

export type ItemSelectorProps<T extends Option> = ItemsProps<T> &
  ItemSelectProps<T> &
  SelectCustomProps

// Note: Typescript is not able to infer types for chakra-react-select some reason.
// Have to explicitly type the generics: https://react-select.com/typescript
type SelectProps<T extends Option> = CreatableProps<T, boolean, GroupBase<T>> &
  SelectCustomProps

function useCustomChakraSelectProps<T extends Option>(): SelectProps<T> {
  return useChakraSelectProps({
    chakraStyles: {
      container: (provided, state) => ({
        ...provided,
        w: "100%"
      }),
      option: (provided, state) => ({
        ...provided,
        color: "gray.900",
        w: "initial",
        bg:
          state.isFocused && state.isSelected
            ? "gray.300"
            : state.isFocused
            ? "gray.200"
            : state.isSelected
            ? "gray.100"
            : "transparent"
      }),
      menu: (provided, state) => ({
        ...provided,
        borderRadius: "md"
      }),
      menuList: (provided, state) => ({
        ...provided,
        padding: 0,
        border: "none",
        borderRadius: "md"
      }),
      placeholder: (provided) => ({
        ...provided,
        paddingLeft: "2px",
        fontSize: { base: "sm", md: undefined },
        noOfLines: 1
        // Make room for the item icon to the right of the dropdown indicator.
        // ml: `calc(var(--input-height) - var(--input-padding))`
      }),
      control: (provided, state) => ({
        ...provided,
        padding: "0 .4em",
        border: "1px solid",
        borderColor: "gray.100",
        color: "gray.900",
        borderRadius: "20px",
        background: state.isFocused
          ? "whiteAlpha.200 !important"
          : "transparent",
        _hover: {
          background: "whiteAlpha.100"
        }
      }),
      clearIndicator: (provided) => ({
        ...provided,
        display: "none"
      }),
      dropdownIndicator: (provided) => ({
        ...provided,
        // Make room for the tooltip icon to the right of the dropdown indicator.
        mr: `calc(var(--input-height) - var(--input-padding))`
      }),
      valueContainer: (provided) => ({
        ...provided,
        pl: 0
      })
    }
  })
}

function ItemSelector<T extends SelectedResource>(
  props: ItemSelectorProps<T>
): JSX.Element
function ItemSelector<T extends SelectedCohort>(
  props: ItemSelectorProps<T>
): JSX.Element
function ItemSelector<T extends Option>({
  options,
  value,
  onChange,
  isLoading,
  itemLabel,
  getNewOptionData,
  mode = "create-or-select",
  icon,
  ...selectProps
}: ItemSelectorProps<T>) {
  const selectRef = useRef<SelectInstance<T, boolean, GroupBase<T>>>(null)
  const [inputValue, setInputValue] = useState<string>("")
  // Track whether the user is explicitly creating a new option via the `Create "<inputValue>"`
  // method in the select menu.
  const [explicitlyCreatingOption, setExplicitlyCreatingOption] =
    useState(false)

  // If there are no options, we override with "create" mode.
  if (!options?.length) mode = "create"

  // Auto-select the single option if it exists when the user only has the select option.
  const autoSelectSingleOption =
    !isLoading && options?.length === 1 && mode === "select"

  // Auto-select item when there is only 1.
  useEffect(() => {
    if (autoSelectSingleOption) {
      handleChange(options[0])
    }
  }, [autoSelectSingleOption])

  function handleChange(selected: SingleValue<T> | MultiValue<T>) {
    // We know we're only dealing with single value selection (not enabling multi-select).
    let item = (selected || null) as SingleValue<T> | null

    onChange(item)
    setInputValue("")
  }

  useEffect(() => {
    // Open the menu after clearing the value.
    // Ok, to call this on mountn with no value as it appears,
    // the select props override this.
    if (!value && !selectProps.isDisabled) {
      selectRef.current?.focus()
      // Likely not necessary to force the menu to open since
      // we are opening menu on focus.
      selectRef.current?.onMenuOpen()
    }
  }, [value])

  // Get the roleOption for a given item.
  function getItemRoleOptions(
    option: T,
    roleKey: "resourceRole" | "cohortRole"
  ) {
    return getRoleOptions().filter((roleOption) => {
      // Serialize the roleOptions to registration roles.
      const roles = serializeRoleBadgesToRegistrationRoles([roleOption])
      // Get the desired registration role.
      const registrationRoleOption = roles[roleKey]
      // Compare roles.
      return option.role === registrationRoleOption
    })
  }

  function formatOptionLabel(option: T, meta: FormatOptionLabelMeta<T>) {
    return meta.context === "menu" ? (
      <VStack spacing={0} w="100%">
        <HStack w="100%" justifyContent="flex-start">
          {"image" in option ? (
            <HStack alignItems="center" w="100%">
              <Image
                src={option.image}
                maxW="50px"
                borderRadius="md"
                alignSelf="flex-start"
                fallback={
                  <Box w="50px" h="37.5px" bg="gray.200" borderRadius="md" />
                }
              />
              {option.id === -1 ? (
                <Text as="span">{`Create "${inputValue}"`}</Text>
              ) : (
                <Text as="span" mr="auto">
                  {option.name}
                </Text>
              )}
              {!!option.role && (
                <HStack flexGrow={1} justifyContent="flex-end">
                  {getItemRoleOptions(option, "resourceRole").map(
                    (roleOption) => (
                      <RoleBadge
                        key={roleOption.value}
                        badge={roleOption.badge}
                        iconOnly
                      />
                    )
                  )}
                </HStack>
              )}
            </HStack>
          ) : option.id === -1 ? (
            <Text as="span">{`Create "${inputValue}"`}</Text>
          ) : (
            <HStack w="100%">
              <GroupAvatars
                group={option}
                showGroupName
                fontColor="blackAlpha.900"
                noOfLines={1}
              />
              {!!option.role && (
                <HStack flexGrow={1} justifyContent="flex-end">
                  {getItemRoleOptions(option, "cohortRole").map(
                    (roleOption) => (
                      <RoleBadge
                        key={roleOption.value}
                        badge={roleOption.badge}
                        iconOnly
                      />
                    )
                  )}
                </HStack>
              )}
            </HStack>
          )}
        </HStack>
      </VStack>
    ) : "image" in option ? (
      <HStack>
        <Image
          src={option.image}
          maxH="24px"
          borderRadius="md"
          fallback={<Box maxH="24px" bg="gray.200" borderRadius="md" />}
        />
        <Text as="span">{option.name}</Text>
      </HStack>
    ) : (
      <GroupAvatars group={option} showGroupName />
    )
  }

  // Common util for filtering options by name.
  function filterOptionName(option: T, inputValue: string) {
    return option.name.toLowerCase().indexOf(inputValue.toLowerCase()) > -1
  }

  // Perform filtering by search value.
  // TODO: custom handle search? Maybe try "match-sorter" for searching in state.
  // Though, "match-sorter" may be overkill if all we're searching is the item's name.
  function filterOption(option: FilterOptionOption<T>, inputValue: string) {
    return filterOptionName(option.data, inputValue)
  }

  function handleInputChange(inputValue: string, action: InputActionMeta) {
    // only set the input when the action that caused the
    // change equals to "input-change" and ignore the other
    // ones like: "set-value", "input-blur", and "menu-close"
    if (action.action !== "input-blur") {
      setInputValue(inputValue) // <---
    }
  }

  const chakraSelectProps = useCustomChakraSelectProps<T>()
  const selectComponents = useMemo(
    () => getSelectCustomComponents<T, boolean, SelectCustomProps>(),
    []
  )

  // Reset the explicitly creating option state when input value changes.
  // This allows the user to once again see the select menu if options exist.
  useEffect(() => {
    setExplicitlyCreatingOption(false)
  }, [inputValue])

  // Whether the user is creating a new option.
  const isCreatingNewOption = useMemo(() => {
    // If there are no options, the user is creating a new one.
    if (!options) {
      return true
    }

    // If user is explicitly creating a new option via the CreateableSelect method, the user is creating a new option.
    if (explicitlyCreatingOption) return true

    // If the user is typing a new option, one that doesn't exist yet, they are creating a new one.
    // The check includes partial matches, so we use the CreatableSelect to allow the user to
    // explicitly create a new option that partially matches existing ones.
    if (
      !!inputValue &&
      !options.filter((item) => filterOptionName(item, inputValue)).length
    ) {
      return true
    }

    // By default, the user is not creating a new option.
    return false
  }, [options, explicitlyCreatingOption, inputValue])

  useEffect(() => {
    if (!inputValue) {
      if (value?.id === -1 && mode !== "create") {
        // Clearing the value when the input is empty and the value is the create option.
        onChange()
      }
    } else if (isCreatingNewOption && mode !== "select") {
      // Ensure value isn't an existing item (which it may have been before
      // user started typing some custom name). If it is an existin item,
      // we need to create a new one.
      const data =
        !value || isExistingItem(value)
          ? getNewOptionData(inputValue, null)
          : value
      if (data) {
        // Auto-select the create option if there are no matching items.
        onChange({
          ...data,
          name: inputValue
        })
      }
    }
  }, [isCreatingNewOption, inputValue])

  // Conditionally clear the icon when the value contains an image since
  // we'll the image should replace the icon (visually).
  if (value && "image" in value && !inputValue) {
    icon = undefined
  }

  // When user is creating a new option, we swap out the Select for an Input.
  // This allows the user to type in a new option. We could use the CreatableSelect
  // component, but it isn't as flexible or user-friendly (clearing the new option
  // isn't intuitive, for example).
  return (isCreatingNewOption && mode !== "select") || mode === "create" ? (
    <ItemInput
      value={value!}
      onChange={(value) => {
        // Let's handle updating both the input value and the option value.
        onChange(value as T)
        setInputValue(value.name)
      }}
      placeholder={
        (selectProps.placeholder as string) || `Create a ${itemLabel}`
      }
      isDisabled={selectProps.isDisabled}
      icon={icon}
    />
  ) : (
    <CreatableSelect
      ref={selectRef}
      options={options}
      value={value}
      inputValue={inputValue}
      // Required for telling react-select how to determine which option is selected.
      getOptionValue={(option) => option.id + ""}
      onChange={handleChange}
      onInputChange={handleInputChange}
      formatOptionLabel={formatOptionLabel}
      filterOption={filterOption}
      placeholder={
        mode === "create-or-select"
          ? `Select or create a ${itemLabel}`
          : `Select a ${itemLabel}`
      }
      aria-label={`Select ${itemLabel}`}
      variant="unstyled"
      isClearable={true}
      escapeClearsValue
      hideSelectedOptions={false}
      isSearchable={true}
      autoFocus={!value || !autoSelectSingleOption}
      openMenuOnFocus={!value || !autoSelectSingleOption}
      closeMenuOnSelect={true}
      backspaceRemovesValue={true}
      isLoading={isLoading}
      loadingMessage={() => null}
      noOptionsMessage={() => null}
      createOptionPosition="first"
      getNewOptionData={getNewOptionData}
      onCreateOption={(value) => {
        const item = getNewOptionData(value, null)
        onChange(item)
        setInputValue(item.name)
        setExplicitlyCreatingOption(true)
      }}
      icon={icon}
      {...{
        ...chakraSelectProps,
        components: {
          ...chakraSelectProps,
          ...selectComponents
        }
      }}
      {...selectProps}
    />
  )
}

export default ItemSelector
